import { genericViewQueryAsText } from '@/shared/helpers/api';
import { http } from '@/shared/httpWrapper';
import _ from 'lodash';

export interface EnsFormatElement {
    elementNo: number;
    identity: string | null;
    isValid: boolean;
    description: string;
    comments?: string | null;
    show: boolean;
    required: boolean;
    include: boolean;
    layout: string;
    reference: string;
    minimumCharacters: number;
    maximumCharacters: number;
    constantValue: string;
    padding?: string | null;
}

export function newFormatElement(ensType: string, elementNo: number) {
    const element: EnsFormatElement = {
        elementNo,
        isValid: true,
        description: '',
        comments: null,
        identity: null,
        required: false,
        maximumCharacters: 1,
        minimumCharacters: 1,
        constantValue: '',
        layout: 'Z',
        reference: '0',
        show: false,
        include: false
    };
    if (ensType === 'TagFormat') {
        element.padding = null;
    }
    return element;
}

export enum ReferencePresence {
    Mandatory = 'Mandatory',
    Recommended = 'Recommended',
    Normal = 'Normal'
}

export function applyReferenceChange(element: EnsFormatElement, referencePresence?: string) {
    if (referencePresence === ReferencePresence.Mandatory || referencePresence === ReferencePresence.Recommended) {
        element.required = true;
    }

    if (element.reference === '9') {
        element.layout = 'N';
    }
}

type SortableFormat = {
    description: string;
    displaySequenceNo?: number | null;
};

export function sortFormatsForDisplay<T extends SortableFormat>(formats: T[]): T[] {
    return _.sortBy(formats, [
        format => format.displaySequenceNo ?? Number.POSITIVE_INFINITY,
        'description'
    ]);
}

type SequencedFormat = {
    name: string;
    displaySequenceNo: number | null;
};

export type DisplaySequenceChange = {
    name: string;
    displaySequenceNo: number | null;
};

/**
 * Numbers the ordered formats 1..N and clears the number of the unordered ones,
 * and returns only the formats whose number changes.
 */
export function getDisplaySequenceChanges(ordered: SequencedFormat[], unordered: SequencedFormat[]): DisplaySequenceChange[] {
    const wanted = [
        ...ordered.map((format, index) => ({ format, displaySequenceNo: index + 1 })),
        ...unordered.map(format => ({ format, displaySequenceNo: null }))
    ];

    return wanted
        .filter(({ format, displaySequenceNo }) => format.displaySequenceNo !== displaySequenceNo)
        .map(({ format, displaySequenceNo }) => ({ name: format.name, displaySequenceNo }));
}

type CategoriesQueryRow = {
    identity: string;
    description: string;
    facility?: string;
};

type FormatsQueryRow = {
    name: string;
    identity: string;
    isValid: boolean;
    description: string;
    facility: string;
    category: string;
    syntax: string | null;
    regex: string | null;
    regexGrouped: string | null;
    uiExplanation: string | null;
    isOfficial: boolean | null;
    displaySequenceNo: number | null;
};

type FormatElementsQueryRow = {
    name: string;
    identity: string | null;
    isValid: boolean;
    description: string;
    comments: string | null;
    minimumCharacters: number | null;
    maximumCharacters: number | null;
    include: boolean | null;
    show: boolean | null;
    required: boolean | null;
    constantValue: string | null;
    layout: string | null;
    reference: string | null;
    padding?: string | null;
};

type LayoutsQueryRow = {
    name: string;
    identity: string;
    description: string;
};

type ReferencesQueryRow = {
    name: string;
    identity: string;
    description: string;
    presence: string;
};

type PaddingsResponse = {
    identity: string;
    description: string;
};

export const queries = {
    async fetchCategories(ensType: string) {
        const query = ensType === 'TagFormat'
            ? 'FROM TagCategory SELECT identity, description, scope as facility WHERE IsValid = true ORDER BY description'
            : 'FROM DocCategory SELECT identity, description, scope as facility WHERE IsValid = true ORDER BY description';
        return genericViewQueryAsText<CategoriesQueryRow>(http, query).then(x => x.data);
    },
    async fetchFormats(ensType: string, selectedFacility: string) {
        const query = ensType === 'TagFormat'
            ? `FROM TagFormat
                        SELECT
                            identity, name, isValid, description, scope as facility, tagCategory as category, syntax,
                            regex, regexGrouped, uiExplanation, isOfficial, displaySequenceNo
                        WHERE isValid = true AND scope = @scope
                        ORDER BY description`
            : `FROM DocumentNumberingFormat
                        SELECT
                            identity, name, isValid, description, scope as facility, docCategory as category, syntax,
                            regex, regexGrouped, uiExplanation, isOfficial, displaySequenceNo
                        WHERE isValid = true AND scope = @scope
                        ORDER BY description`;
        return genericViewQueryAsText<FormatsQueryRow>(http, query, [
            { name: '@scope', value: selectedFacility }
        ]).then(x => x.data);
    },
    async fetchLayouts(ensType: string) {
        const query = ensType === 'TagFormat'
            ? 'FROM EnsFormatElementLayout SELECT name, identity, description WHERE IsValid = true ORDER BY name'
            : 'FROM EnsDocumentElementLayout SELECT name, identity, description WHERE IsValid = true ORDER BY name';
        return genericViewQueryAsText<LayoutsQueryRow>(http, query, undefined).then(x => x.data);
    },
    async fetchReferences(ensType: string) {
        const query = ensType === 'TagFormat'
            ? 'FROM TagFormatReference SELECT name, identity, description, presence WHERE IsValid = true ORDER BY description'
            : 'FROM DocumentNumberingFormatReference SELECT name, identity, description, presence WHERE IsValid = true ORDER BY description';
        return genericViewQueryAsText<ReferencesQueryRow>(http, query, undefined).then(x => x.data);
    },
    async fetchPaddings(ensType: string) {
        if (ensType !== 'TagFormat')
            throw new Error('Paddings are only available for TagFormats');

        const query = 'FROM EnsPadding SELECT identity, description WHERE IsValid = true ORDER BY description';
        return genericViewQueryAsText<PaddingsResponse>(http, query, undefined).then(x => x.data);
    },
    async fetchFormatElements(ensType: string, formatIdentity: string, selectedFacility: string) {
        const query = ensType === 'TagFormat'
            ? `FROM TagFormatElement
                        SELECT
                            name, isValid, description, comments, minimumCharacters, maximumCharacters, inTagNo as include,
                            show, required, constantValue, layout, reference, padding, identity
                        WHERE tagFormat = "${formatIdentity}" AND scope = @facility`
            : `FROM DocumentNumberingFormatElement
                        SELECT
                            name, isValid, description, comments, minimumCharacters, maximumCharacters, inDocNo as include,
                            show, required, constantValue, layout, reference, identity
                        WHERE docNoFormat = "${formatIdentity}" AND scope = @facility`;

        return genericViewQueryAsText<FormatElementsQueryRow>(http, query, [
            { name: '@facility', value: selectedFacility }
        ]).then(x => x.data);
    }
};
